import React, { useEffect, useState } from 'react';
import { Row, Col } from 'antd';
import PropTypes from 'prop-types';
import { I18n } from '@aws-amplify/core';
import hooks from '@/components/views/PreliminaryAnalysis/hooks';
import { ActionPlanModal } from '@/components/ui/Modals/ActionPlanModal';
import { useGetActionPlanFromReport } from '@/hooks/v2/useGetActionPlanFromReport';
import { usePreliminaryAnalysis } from '@/components/views/PreliminaryAnalysis/context';
import {
	Container,
	TitleCard,
	ButtonCard,
	ResumeCard,
	CircleFilledIcon,
	WarningFilledIcon,
	ExclamationFilledIcon
} from './styles';

export const enumRiskMap = {
	low: 1,
	moderate: 2,
	high: 3,
	very_high: 4,
	extreme: 5
};

const { useFindPreliminaryAnalysisByStepName } = hooks;

export const RiskLevelCard = ({ step, value }) => {
	const [levelRisk, setLevelRisk] = useState(value);
	const [createActionPlan, setCreateActionPlan] = useState(false);
	const { file, organization_id, company_id, preliminaryAnalysis: reportData } = usePreliminaryAnalysis();

	const { data: preliminaryAnalysis, isFetching: fetchingPreliminaryAnaliysis } =
		useFindPreliminaryAnalysisByStepName(
			organization_id,
			company_id,
			file?.id,
			JSON.stringify(step),
			levelRisk,
			file?.preliminary_analysis?.id
		);

	const { data: actionPlan, isFetching: fetchingActionPlan } = useGetActionPlanFromReport({
		company_id: company_id,
		organization_id: organization_id,
		file_id: preliminaryAnalysis.fileId ?? '',
		preliminary_analysis_step_id: preliminaryAnalysis.stepId
	});

	const iconList = {
		[enumRiskMap.low]: <CircleFilledIcon />,
		[enumRiskMap.moderate]: <ExclamationFilledIcon ismoderate="true" />,
		[enumRiskMap.high]: <ExclamationFilledIcon />,
		[enumRiskMap.very_high]: <WarningFilledIcon />,
		[enumRiskMap.extreme]: <WarningFilledIcon />
	};

	const titleList = {
		[enumRiskMap.low]: 'Acceptable',
		[enumRiskMap.moderate]: 'Moderate',
		[enumRiskMap.high]: 'High',
		[enumRiskMap.very_high]: 'Very High',
		[enumRiskMap.extreme]: 'Serious and Imminent'
	};

	const descriptionList = {
		[enumRiskMap.low]: 'Ok',
		[enumRiskMap.moderate]: 'Improvement action',
		[enumRiskMap.high]: 'Immediate action for improvement and reassess',
		[enumRiskMap.very_high]: 'Perform Ergonomic Work Analysis',
		[enumRiskMap.extreme]: 'Stop the activity and carry out an Ergonomic Work Analysis'
	};

	useEffect(() => {
		setLevelRisk(value);
	}, [value]);

	function onCreateActionPlan() {
		setCreateActionPlan((prev) => !prev);
	}

	return (
		<Container gutter={[10, 0]} $levelRisk={levelRisk}>
			<Col span={3} style={{ textAlign: 'center' }}>
				{iconList[levelRisk] || <ExclamationFilledIcon />}
			</Col>
			<Col span={21}>
				<Row>
					<Col span={24}>
						<TitleCard $levelRisk={levelRisk} level={5}>
							{I18n.get(!levelRisk ? 'Fill in the details above' : `${titleList[levelRisk]} Risk`)}
						</TitleCard>
					</Col>
					<Col span={24}>
						<ResumeCard $levelRisk={levelRisk}>{I18n.get(descriptionList[levelRisk])}</ResumeCard>
					</Col>
					<Col span={24} style={{ paddingTop: '5%', ...(!levelRisk && { display: 'none' }) }}>
						<ButtonCard
							key={step}
							loading={fetchingActionPlan}
							$levelRisk={levelRisk}
							size="small"
							disabled={fetchingActionPlan}
							onClick={onCreateActionPlan}
						>
							{actionPlan?.id && !fetchingPreliminaryAnaliysis && !fetchingActionPlan
								? I18n.get('Update action plan')
								: I18n.get('Create action plan')}
						</ButtonCard>
					</Col>
				</Row>
			</Col>

			{step?.length > 0 && !fetchingActionPlan && (
				<ActionPlanModal
					companyId={company_id}
					actionPlan={actionPlan}
					isOpen={createActionPlan}
					onClose={onCreateActionPlan}
					organizationId={organization_id}
					score={preliminaryAnalysis.score}
					result={preliminaryAnalysis.result}
					fileId={preliminaryAnalysis.fileId}
					preliminaryAnalysisStepId={preliminaryAnalysis.stepId}
					activityId={reportData?.data?.activity_id || file?.preliminary_analysis?.activity_id}
				/>
			)}
		</Container>
	);
};

RiskLevelCard.propTypes = {
	levelRisk: PropTypes.oneOf([1, 2, 3, 4, 5])
};
